import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Dropdown } from 'antd';
import { useStore } from '../../../app/data/root.store';
import {
  VaultBulkEditCancelIcon,
  VaultBulkEditDownloadIcon,
  VaultBulkEditTagIcon,
  VaultBulkEditDeleteIcon,
  VaultBulkEditArrowDownIcon,
  VaultBulkEditArrowRightIcon
} from '../../assets/icons';
import { DeleteConfirmationModal } from '../VaultMetadataPanel/DeleteConfirmationModal';
import { PeriodEditModal } from './modals/PeriodEditModal';
import { TagsEditModal } from './modals/TagsEditModal';
import { LinkedItemsEditModal } from './modals/LinkedItemsEditModal';
import {
  VaultBulkEditActionButton,
  VaultBulkEditActionButtonText,
  VaultBulkEditHeaderCancelEditButton,
  VaultBulkEditHeaderCancelEditButtonText,
  VaultBulkEditHeaderText,
  VaultBulkEditHeaderWrapper,
  VaultCancelButtonIconWrapper,
  VaultTagsDropdownMenu,
  VaultTagsDropdownMenuItem,
} from './VaultBulkEditRow.styles';
import { ModifiedTooltip } from '../../../../common/components/ModifiedTooltip/ModifiedTooltip';
import { ActionLockedIconWithTooltip } from '../../../../common/components/ActionLocked/ActionLockedIconWithTooltip';

export const VaultBulkEditRow = observer(() => {
  const { vaultStore, userStore } = useStore();
  const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [currentModal, setCurrentModal] = useState(null);

  const handleSaveBulkEditChanges = async (value) => {
    try {
      await vaultStore.saveBulkEditChanges(value);
      setCurrentModal(null);
    } catch (error) {
      console.error('Error saving bulk edit changes:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (userStore.isActionBlockedByRole('deleteVaultFile')) {
      userStore.setShowLimitedSharedAccess(true);
      return;
    }
    try {
      await vaultStore.bulkDeleteFiles();
      setIsShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting files:', error);
    }
  };

  const onEditAction = (action) => {
    if (['tags', 'periods', 'linkedItems'].includes(action)) {
      vaultStore.setSelectedAttributeToEdit(action);
      setCurrentModal(action);
      setIsTagsDropdownOpen(false);
    } else if (action === 'delete') {
      if (userStore.isActionBlockedByRole('deleteVaultFile')) {
        userStore.setShowLimitedSharedAccess(true);
        return;
      }
      setIsShowDeleteModal(true);
    } else if (action === 'download') {
      vaultStore.bulkDownloadFiles();
    }
  };

  const handleCancelEditMode = () => {
    vaultStore.exitBulkEditMode();
  };

  const handleTagsDropdownOpenChange = () => {
    if (userStore.isActionBlockedByRole('editVaultFileMetadata')) {
      userStore.setShowLimitedSharedAccess(true);
      return;
    }
  };

  const renderTagsDropdown = () => (
    <VaultTagsDropdownMenu>
      <VaultTagsDropdownMenuItem onClick={() => onEditAction('periods')}>
        <span>Period</span>
        <VaultBulkEditArrowRightIcon />
      </VaultTagsDropdownMenuItem>

      <VaultTagsDropdownMenuItem onClick={() => onEditAction('tags')}>
        <span>Tags</span>
        <VaultBulkEditArrowRightIcon />
      </VaultTagsDropdownMenuItem>

      <VaultTagsDropdownMenuItem onClick={() => onEditAction('linkedItems')}>
        <span>Linked items</span>
        <VaultBulkEditArrowRightIcon />
      </VaultTagsDropdownMenuItem>
    </VaultTagsDropdownMenu>
  );

  return (
    <>
      <VaultBulkEditHeaderWrapper>

        <VaultBulkEditHeaderText>
          {vaultStore.selectedFilesForBulkEdit.length === 0 ? 'Select files to edit' : `Edit ${vaultStore.selectedFilesForBulkEdit.length} selected file${vaultStore.selectedFilesForBulkEdit.length === 1 ? '' : 's'}`}
        </VaultBulkEditHeaderText>

        <div style={{ display: 'flex', gap: '8px' }}>
          <Dropdown
            overlay={renderTagsDropdown()}
            trigger={['click']}
            open={isTagsDropdownOpen}
            disabled={userStore.isActionBlockedByRole('editVaultFileMetadata')}
            onOpenChange={setIsTagsDropdownOpen}
            placement="topLeft"
            overlayClassName="vault-bulk-edit-tags-dropdown"
            getPopupContainer={(triggerNode) => triggerNode.parentNode || document.body}
          >
            <ModifiedTooltip
              title="Edit tags, periods, or linked items for selected files"
              overlayStyle={{ maxWidth: '250px' }}
              placement="left"
            >
              <VaultBulkEditActionButton
                disabled={vaultStore.selectedFilesForBulkEdit.length === 0}
                className={isTagsDropdownOpen ? 'active' : ''}
                onClick={handleTagsDropdownOpenChange}
              >
                <VaultBulkEditTagIcon />
                <VaultBulkEditActionButtonText>
                  Labels
                </VaultBulkEditActionButtonText>
                {userStore.isActionBlockedByRole('editVaultFileMetadata') ? <ActionLockedIconWithTooltip iconColor={'#ACB1BB'} isRoleBlocked={true} marginLeft={'0px'} /> : <VaultBulkEditArrowDownIcon />}
              </VaultBulkEditActionButton>
            </ModifiedTooltip>
          </Dropdown>

          <ModifiedTooltip
            title="Download selected files"
          >
            <VaultBulkEditActionButton
              onClick={() => onEditAction('download')}
              disabled={vaultStore.selectedFilesForBulkEdit.length === 0}
            >
              <VaultBulkEditDownloadIcon />
              <VaultBulkEditActionButtonText>Download</VaultBulkEditActionButtonText>
            </VaultBulkEditActionButton>
          </ModifiedTooltip>

          <ModifiedTooltip
            title="Delete selected files"
          >
            <VaultBulkEditActionButton
              onClick={() => onEditAction('delete')}
              disabled={vaultStore.selectedFilesForBulkEdit.length === 0}
            >
              <VaultBulkEditDeleteIcon />
              <VaultBulkEditActionButtonText>
                Delete
                {userStore.isActionBlockedByRole('deleteVaultFile') && <ActionLockedIconWithTooltip iconColor={'#ACB1BB'} isRoleBlocked={true} marginLeft={'0px'} />}
              </VaultBulkEditActionButtonText>
            </VaultBulkEditActionButton>
          </ModifiedTooltip>
        </div>

        <ModifiedTooltip
          title="Cancel edit mode"
        >
          <VaultBulkEditHeaderCancelEditButton onClick={handleCancelEditMode}>
            <VaultCancelButtonIconWrapper>
              <VaultBulkEditCancelIcon />
            </VaultCancelButtonIconWrapper>
            <VaultBulkEditHeaderCancelEditButtonText>Cancel edit mode</VaultBulkEditHeaderCancelEditButtonText>
          </VaultBulkEditHeaderCancelEditButton>
        </ModifiedTooltip>
      </VaultBulkEditHeaderWrapper>

      {currentModal === 'periods' && (
        <PeriodEditModal
          onClose={() => setCurrentModal(null)}
          onSave={handleSaveBulkEditChanges}
        />
      )}

      {currentModal === 'tags' && (
        <TagsEditModal
          onClose={() => setCurrentModal(null)}
          onSave={handleSaveBulkEditChanges}
        />
      )}

      {currentModal === 'linkedItems' && (
        <LinkedItemsEditModal
          onClose={() => setCurrentModal(null)}
          onSave={handleSaveBulkEditChanges}
        />
      )}

      {isShowDeleteModal && (
        <DeleteConfirmationModal
          onCancel={() => setIsShowDeleteModal(false)}
          onConfirm={handleBulkDelete}
          fileCount={vaultStore.selectedFilesForBulkEdit.length}
        />
      )}
    </>
  );
});

